// F1 Season Detail Page
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useF1Championship } from '../../hooks/useF1Championship';
import F1PageTemplate from '../../components/f1/F1PageTemplate';
import { TableChart, GradientChart } from '../../components/charts';
import LoadingSpinner from '../../components/LoadingSpinner';

const F1SeasonDetailPage = () => {
  const { year } = useParams();
  const { data, loading, error } = useF1Championship();

  if (loading) {
    return (
      <div className="min-h-screen theme-bg-secondary flex items-center justify-center">
        <LoadingSpinner message="Loading Season Data..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen theme-bg-secondary flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-semibold text-red-600 mb-4">Error Loading Season</div>
          <div className="theme-secondary">{error}</div>
          <Link to="/f1/seasons" className="mt-4 inline-block px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">
            Back to Seasons
          </Link>
        </div>
      </div>
    );
  }

  // Find specific season
  const season = data?.seasonSummaries?.find(s => s.year == year);
  
  if (!season) {
    return (
      <div className="min-h-screen theme-bg-secondary flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-semibold theme-primary mb-4">Season Not Found</div>
          <div className="theme-secondary mb-4">Season {year} could not be found.</div>
          <Link to="/f1/seasons" className="inline-block px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">
            Back to Seasons
          </Link>
        </div>
      </div>
    );
  }

  // Get races for this season
  const seasonRaces = data?.seasonRaces?.filter(race => race.year == year) || [];

  // Transform race data to make names clickable
  const transformedRaces = seasonRaces.map(race => ({
    ...race,
    name: (
      <Link 
        to={`/f1/races/${race.raceId}`}
        className="theme-primary hover:theme-accent hover:underline font-medium"
      >
        {race.name}
      </Link>
    ),
    circuit: (
      <Link 
        to={`/f1/circuits/${race.circuitId}`}
        className="theme-primary hover:theme-accent hover:underline font-medium"
      >
        {race.circuit}
      </Link>
    ),
    podium: race.podiumDrivers && race.podiumDrivers.length > 0 ? (
      <div className="space-y-1">
        {race.podiumDrivers.map((driver, index) => (
          <div key={driver.driverId} className="flex items-center space-x-2">
            <span className="text-xs theme-secondary font-medium">
              {index + 1}{index === 0 ? 'st' : index === 1 ? 'nd' : 'rd'}
            </span>
            <Link 
              to={`/f1/drivers/${driver.driverId}`}
              className="theme-primary hover:theme-accent hover:underline text-sm"
            >
              {driver.driverName}
            </Link>
          </div>
        ))}
      </div>
    ) : race.podium
  }));

  const stats = [
    { value: season.year, label: 'Season', color: 'theme-primary' },
    { value: season.raceCount, label: 'Total Races', color: 'theme-primary' },
    { value: season.topDrivers?.length || 0, label: 'Drivers with Points', color: 'theme-primary' },
    { value: season.topDrivers?.[0]?.points || 0, label: 'Champion Points', color: 'theme-primary' },
    { value: season.topDrivers?.[0]?.driverName || 'Unknown', label: 'Season Champion', color: 'theme-primary' },
    { value: season.totalPoints || 0, label: 'Total Points', color: 'theme-primary' }
  ];

  return (
    <F1PageTemplate
      title={`${season.year} Season Overview`}
      subtitle={`Complete Grand Prix results and championship standings for the ${season.year} F1 season`}
      summary={['Race results', 'Podium finishers', 'Championship points']}
    >
      
      <div className="space-y-8">
        {/* Season Overview */}
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">            
            {/* Champion Card */}
            <div className="card">
              <h4 className="card-title">Season Champion</h4>
              <div className="space-y-2">
                <div className="text-center">
                  <div className="text-2xl font-bold theme-accent mb-1">
                    {season.topDrivers?.[0] ? (
                      <Link 
                        to={`/f1/drivers/${season.topDrivers[0].driverId}`}
                        className="theme-accent hover:underline"
                      >
                        {season.topDrivers[0].driverName}
                      </Link>
                    ) : 'Unknown'}
                  </div>
                  <div className="text-sm theme-secondary">Champion Points</div>
                  <div className="text-xl font-semibold theme-primary">
                    {season.topDrivers?.[0]?.points || 0}
                  </div>
                </div>
              </div>
            </div>
            {/* Champion Card */}
            <div className="card">
              <h4 className="card-title">Second Place</h4>
              <div className="space-y-2">
                <div className="text-center">
                  <div className="text-2xl font-bold theme-accent mb-1">
                    {season.topDrivers?.[1] ? (
                      <Link 
                        to={`/f1/drivers/${season.topDrivers[1].driverId}`}
                        className="theme-accent hover:underline"
                      >
                        {season.topDrivers[1].driverName}
                      </Link>
                    ) : 'Unknown'}
                  </div>
                  <div className="text-sm theme-secondary">Champion Points</div>
                  <div className="text-xl font-semibold theme-primary">
                    {season.topDrivers?.[1]?.points || 0}
                  </div>
                </div>
              </div>
            </div>
            {/* Champion Card */}
            <div className="card">
              <h4 className="card-title">Third Place</h4>
              <div className="space-y-2">
                <div className="text-center">
                  <div className="text-2xl font-bold theme-accent mb-1">
                    {season.topDrivers?.[2] ? (
                      <Link 
                        to={`/f1/drivers/${season.topDrivers[2].driverId}`}
                        className="theme-accent hover:underline"
                      >
                        {season.topDrivers[2].driverName}
                      </Link>
                    ) : 'Unknown'}
                  </div>
                  <div className="text-sm theme-secondary">Champion Points</div>
                  <div className="text-xl font-semibold theme-primary">
                    {season.topDrivers?.[2]?.points || 0}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Championship Standings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Driver Standings */}
          {season.allDrivers && season.allDrivers.length > 0 && (
            <GradientChart 
              data={season.allDrivers.map(driver => ({
                value: driver.points,
                label: driver.driverName,
                id: driver.driverId
              }))}
              title={`${season.year} Driver Championship Standings`}
              subtitle="All Drivers by Championship Points"
              className="w-full"
              linkTo="/f1/drivers"
              linkKey="driverId"
              actionLink={`/f1/seasons/${year}/standings-exploration`}
              actionLinkText="Explore Progression"
            />
          )}

          {/* Constructor Standings */}
          {season.allConstructors && season.allConstructors.length > 0 && (
            <GradientChart 
              data={season.allConstructors.map(constructor => ({
                value: constructor.points,
                label: constructor.constructorName,
                id: constructor.constructorId
              }))}
              title={`${season.year} Constructor Championship Standings`}
              subtitle="All Constructors by Championship Points"
              className="w-full"
              linkTo="/f1/teams"
              linkKey="constructorId"
            />
          )}
        </div>

        {/* Grand Prix Results */}
        {seasonRaces.length > 0 && (
          <TableChart 
            data={transformedRaces}
            title="Grand Prix Results"
            subtitle={`All races from ${season.year} F1 season`}
            config={{
              columns: [
                { key: 'date', label: 'Date', width: 100 },
                { key: 'round', label: 'Round', width: 60 },
                { key: 'name', label: 'Grand Prix', width: 200 },
                { key: 'circuit', label: 'Circuit', width: 180 },
                { key: 'podium', label: 'Podium Drivers', width: 250 },
                { key: 'laps', label: 'Race Laps', width: 100 }
              ],
              sortable: true,
              sortBy: 'round',
              sortDirection: 'asc',
              pagination: true,
              pageSize: 20,
              showHeader: true,
              rowHeight: 60,
              headerHeight: 40,
              showFooter: true,
              footerHeight: 40
            }}
          />
        )}
      </div>
    </F1PageTemplate>
  );
};

export default F1SeasonDetailPage;
